import React, {useEffect, useState} from 'react';
import {
  Box,
  Heading,
  Select,
  Button,
  Input,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  List,
  ListItem,
  Text,
} from '@chakra-ui/react';

import dayjs from 'dayjs';

import {api} from "../../api/userAPI";
import {PerformanceIncentiveTriggers} from "./PerformanceIncentiveTriggers";
import {CompensatoryDatesTable} from "./CompensatoryDatesTable";
import {UserManagementTable} from "./UserManagementTable";
const LIMIT = 30;
const DEBOUNCE_MS = 300;

type RoleType = 'viewer' | 'editor' | 'admin';

type Roles = {
  [user: string]: {
    performanceRole?: RoleType;
    isAdmin?: boolean;
    canImpersonate?: boolean;
  };
};

type User = {
  id: string;
  name: string;
  role: string;
};

// const mockUsers: User[] = Array.from({ length: 700 }, (_, i) => ({
//   id: `user${i + 1}`,
//   name: `User ${i + 1}`,
//   role: "viewer"
// }));


const AdminPanel: React.FC = () => {
  const [roles, setRoles] = useState<Roles>({});

  const [preview, setPreview] = useState<Roles>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<RoleType | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearchTerm), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [userSearchTerm]);

  useEffect(() => {
    api.getUsersBase({ query: debouncedUserSearch || undefined, limit: LIMIT }).then(value => {
      const data: any[] = value.data;
      setUsers(data.map(v => ({ id: v.email, name: v.email, role: v.access })));
    });
  }, [debouncedUserSearch]);

  useEffect(() => {
    api.getActiveManagementUsers().then(value => {
      const data: any[] = value.data;
      let roles: Roles = {};
      data.filter(u => u.access !== null).forEach(u => {
        roles[u.email] = {
          performanceRole: u.access as RoleType,
          isAdmin: u.access === 'admin',
          canImpersonate: u.access === 'admin' || u.access === 'viewer' || u.access === 'editor',
        };
      });
      setRoles(roles);
    });
  }, [refreshIndex]);



const handleRoleChange = (user: string| null, role: RoleType) => {
  if (!user) return
  setPreview((prevPreview) => {
    const updatedRoles = {
      ...prevPreview,
      [user]: {
        performanceRole: role
      },
    };
    return updatedRoles;
  });
};


  const finalizePreview = () => {
    //TODO  Call API
  const roleList = Object.entries(preview).map(([email, roleData]) => ({
    email,
    role: roleData.performanceRole ?? null, // or leave undefined if you prefer
  }));
    api.addUserRoles({"roles": roleList}).then(r =>handleRefresh())
    setPreview({});
  };

  const clearPreview = () => {
    setPreview({});
  };
  const handleRefresh = () => {
    setRefreshIndex(prev => prev + 1); // Trigger re-fetch
  };

  const revokePermission = (userId: string) => {
    api.revokeUserAccess(userId).then(value => handleRefresh())
  };

  const filteredRoles = Object.entries(roles).filter(([userId]) =>
    userId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box p={{base: 2, md: 5}} borderWidth={1} borderRadius="md" shadow="md">
      <Heading textAlign="center" mb={5}>Admin Panel</Heading>
      <Flex justify="center" wrap="wrap" gap={10}>
        {/* Performance Incentive Roles */}
        <Box borderWidth={1} borderRadius="md" p={3} shadow="sm" width={{base: "100%", md: "sm"}} maxW="100%">
          <Heading size="sm" mb={3}>Performance Incentive Roles</Heading>
          <FormControl>
            <FormLabel>Select User</FormLabel>
            <Input
              placeholder="Search users by email"
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              mb={1}
            />
            <Box maxHeight="150px" overflowY="auto" borderWidth={1} borderRadius="md" mb={3}>
              {users.map((user) => (
                <Box
                  key={user.id}
                  px={3}
                  py={1}
                  cursor="pointer"
                  bg={selectedUser === user.id ? 'teal.100' : 'transparent'}
                  _hover={{ bg: selectedUser === user.id ? 'teal.200' : 'surface-subtle' }}
                  _dark={{ bg: selectedUser === user.id ? 'teal.700' : 'transparent', _hover: { bg: selectedUser === user.id ? 'teal.600' : 'surface-subtle' } }}
                  onClick={() => setSelectedUser(user.id)}
                >
                  <Text fontSize="sm">{user.name}</Text>
                </Box>
              ))}
              {users.length === 0 && (
                <Text fontSize="sm" color="text-subtle" p={2}>No users found</Text>
              )}
            </Box>
            <FormLabel mt={3}>Assign Role</FormLabel>
            <Select
              placeholder="Assign Role"
            onChange={(e) => {
              const role = e.target.value as RoleType;
              setSelectedRole(role);
              handleRoleChange(selectedUser, role );
            }}
            >
              <option value="viewer">Viewer</option>
              <option value="editor">Editor</option>
              <option value="admin">Admin</option>
            </Select>
          </FormControl>
          <List mt={3} spacing={2}>
            {Object.entries(preview).map(([userId, role]) => (
              role.performanceRole && (
                <ListItem key={userId}>{userId} - {role.performanceRole}</ListItem>
              )
            ))}
          </List>
          <Flex mt={3} gap={3}>
            <Button colorScheme="teal" size="sm" onClick={finalizePreview}>
              Add to Final List
            </Button>
            <Button colorScheme="red" size="sm" onClick={clearPreview}>
              Clear Preview
            </Button>
          </Flex>
        </Box>


      </Flex>

      {/* Existing Data Table */}
      <Box mt={10} borderWidth={1} borderRadius="md" p={3} shadow="sm" maxHeight="300px" overflowY="auto" overflowX="auto">
        <Heading size="sm" mb={3}>Current Assignments</Heading>
        <Input
          placeholder="Search by user name"
          mb={3}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Table variant="striped" size="sm" whiteSpace={{base: "nowrap", md: "normal"}}>
          <Thead>
            <Tr>
              <Th>User</Th>
              <Th>Performance Role</Th>
              <Th>Is Admin</Th>
              <Th>Can Impersonate</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredRoles.map(([userId, role]) => (
              <Tr key={userId}>
                <Td>{userId}</Td>
                <Td>{role.performanceRole || "-"}</Td>
                <Td>{role.isAdmin ? "Yes" : "No"}</Td>
                <Td>{role.canImpersonate ? "Yes" : "No"}</Td>
                <Td>
                  {role.performanceRole && (
                    <Button size="xs" colorScheme="red" onClick={() => revokePermission(userId)}>Revoke</Button>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {/* User Management Table */}
      <UserManagementTable />

    <Flex flexGrow={1} gap={{base: 0, md: 10}} direction={{base: "column", md: "row"}}>
      {/* Performance Incentive Date Selection */}
      <PerformanceIncentiveTriggers/>
      {/* Compensatory Dates Table */}
      <CompensatoryDatesTable/>
    </Flex>
    </Box>
  );
};

export default AdminPanel;


